/**
 * Team Inbox — S6 Inbox Settings PURE form model (Phase 2).
 *
 * Draft shapes the screen edits + validation + the doc builders the
 * `teamInboxAdmin · settingsUpsert` call submits. All pure / clock-free —
 * locked by test/teaminboxSettingsModel.test.cjs. The server normalises
 * AGAIN on write (defence in depth); this model exists so the admin gets
 * field-level errors instead of a server reject.
 */
import type {
  CannedReply,
  NumberRegistry,
  Teammate,
  WaNumber,
} from './types';

const HHMM_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/* ───────────────────── draft shapes ───────────────────── */

export interface NumberDraft {
  phoneId: string; // '' = not configured yet (7121 until the QR scan)
  line: string;
  label: string;
  colour: string;
  pool: string[]; // staff ids
  enabled: boolean;
}

export interface SettingsDraft {
  numbers: NumberDraft[];
  mode: 'round_robin' | 'manual';
  escalateAfterMin: number;
  fallback: string[]; // ordered chain, ≥1 entries
  workingHoursOn: boolean;
  whStart: string;
  whEnd: string;
  whDays: number[];
  adminScope: 'all' | 'escalations_unassigned';
  quietHoursOn: boolean;
  qhStart: string;
  qhEnd: string;
}

export interface CannedDraft {
  slash: string;
  text: string;
  numberScope: string; // '' = all numbers
}

/* ───────────────────── draft ⇄ registry ───────────────────── */

/** PURE — seed the S6 draft from the live (normalised) registry. */
export function draftFromRegistry(reg: NumberRegistry): SettingsDraft {
  const wh = reg.allocation.workingHours || null;
  const qh = (reg.notifications && reg.notifications.quietHours) || null;
  return {
    numbers: reg.numbers.map((n) => ({
      phoneId: n.phoneId || '',
      line: n.line,
      label: n.label,
      colour: n.colour,
      pool: [...n.pool],
      enabled: n.enabled,
    })),
    mode: reg.allocation.mode,
    escalateAfterMin: reg.allocation.escalateAfterMin,
    fallback: [...reg.allocation.fallback],
    workingHoursOn: !!wh,
    whStart: wh ? wh.start : '09:30',
    whEnd: wh ? wh.end : '18:30',
    whDays: wh ? [...wh.days] : [1, 2, 3, 4, 5, 6],
    adminScope:
      (reg.notifications && reg.notifications.adminScope) ||
      'escalations_unassigned',
    quietHoursOn: !!qh,
    qhStart: qh ? qh.start : '22:00',
    qhEnd: qh ? qh.end : '08:00',
  };
}

/** PURE — validate the whole S6 draft → field-level error list. */
export function validateDraft(d: SettingsDraft): string[] {
  const errors: string[] = [];
  if (!d.numbers.length) errors.push('At least one number is required.');
  d.numbers.forEach((n, i) => {
    const who = n.label || n.line || `number ${i + 1}`;
    if (!n.line.trim()) errors.push(`${who}: line (the +91 number) is required.`);
    if (!n.label.trim()) errors.push(`${who}: role label is required.`);
    if (n.phoneId && !/^\d{6,}$/.test(n.phoneId)) {
      errors.push(`${who}: phone ID must be the numeric Meta phone_number_id.`);
    }
    if (n.enabled && !n.phoneId) {
      errors.push(`${who}: cannot enable a line before its phone ID is configured.`);
    }
    if (n.colour && !HEX_RE.test(n.colour)) {
      errors.push(`${who}: colour must be a #rrggbb hex value.`);
    }
  });
  const seen = new Set<string>();
  for (const n of d.numbers) {
    if (n.phoneId && seen.has(n.phoneId)) {
      errors.push(`Duplicate phone ID ${n.phoneId}.`);
    }
    if (n.phoneId) seen.add(n.phoneId);
  }
  if (
    !Number.isFinite(d.escalateAfterMin) ||
    d.escalateAfterMin < 1 ||
    d.escalateAfterMin > 240
  ) {
    errors.push('Escalation timeout must be between 1 and 240 minutes.');
  }
  if (!d.fallback.filter((f) => f.trim()).length) {
    errors.push('The fallback chain needs at least one member.');
  }
  if (d.workingHoursOn) {
    if (!HHMM_RE.test(d.whStart) || !HHMM_RE.test(d.whEnd)) {
      errors.push('Working hours must be HH:MM times.');
    } else if (d.whStart === d.whEnd) {
      errors.push('Working hours start and end cannot be identical.');
    }
    if (!d.whDays.length) errors.push('Working hours need at least one day.');
  }
  if (d.quietHoursOn) {
    if (!HHMM_RE.test(d.qhStart) || !HHMM_RE.test(d.qhEnd)) {
      errors.push('Quiet hours must be HH:MM times.');
    } else if (d.qhStart === d.qhEnd) {
      errors.push('Quiet hours start and end cannot be identical.');
    }
  }
  return errors;
}

/** PURE — build the settings/wa_numbers doc the upsert call submits. */
export function draftToRegistryDoc(d: SettingsDraft): {
  numbers: WaNumber[];
  allocation: Record<string, unknown>;
  notifications: Record<string, unknown>;
} {
  return {
    numbers: d.numbers.map((n) => ({
      phoneId: n.phoneId.trim() || null,
      line: n.line.trim(),
      label: n.label.trim(),
      colour: n.colour.trim() || '#6b7280',
      pool: n.pool.filter((p) => p.trim()),
      enabled: n.enabled,
    })),
    allocation: {
      mode: d.mode,
      escalateAfterMin: d.escalateAfterMin,
      fallback: d.fallback.map((f) => f.trim()).filter(Boolean),
      workingHours: d.workingHoursOn
        ? {
            start: d.whStart,
            end: d.whEnd,
            days: [...d.whDays].sort(),
            tzOffsetMinutes: 330,
          }
        : null,
    },
    notifications: {
      adminScope: d.adminScope,
      quietHours: d.quietHoursOn ? { start: d.qhStart, end: d.qhEnd } : null,
    },
  };
}

/* ───────────────────── pool membership (alias-aware) ───────────────────── */

/**
 * PURE — does a pool contain this teammate? Pool entries may be staff IDS
 * (what S6 writes going forward) OR legacy NAME slugs ('khilendra' — the
 * boot-fallback form the backend resolver also accepts). Matched against
 * the id, the full name and the first name, case-insensitively, so the
 * checkboxes reflect what the engine actually does.
 */
export function poolHasMember(pool: string[], mate: Teammate): boolean {
  const name = mate.name.trim().toLowerCase();
  const first = name.split(/\s+/)[0] || name;
  return pool.some((p) => {
    const e = p.trim().toLowerCase();
    return e === mate.id.toLowerCase() || e === name || e === first;
  });
}

/** PURE — toggle a teammate in a pool: removes EVERY alias form on
 *  un-tick; adds the canonical staff ID on tick. */
export function togglePoolMember(pool: string[], mate: Teammate): string[] {
  if (poolHasMember(pool, mate)) {
    const name = mate.name.trim().toLowerCase();
    const first = name.split(/\s+/)[0] || name;
    return pool.filter((p) => {
      const e = p.trim().toLowerCase();
      return e !== mate.id.toLowerCase() && e !== name && e !== first;
    });
  }
  return [...pool, mate.id];
}

/* ───────────────────── canned replies ───────────────────── */

/** PURE — validate ONE canned-reply draft (add/edit form). */
export function validateCanned(c: CannedDraft, existing: CannedReply[]): string[] {
  const errors: string[] = [];
  const slug = c.slash
    .trim()
    .toLowerCase()
    .replace(/^\/+/, '')
    .replace(/[^a-z0-9_-]+/g, '');
  if (!slug) errors.push('Shortcut needs letters/digits (e.g. /timing).');
  if (!c.text.trim()) errors.push('Reply text is required.');
  if (c.text.length > 4096) errors.push('Reply text is too long (4096 max).');
  if (slug && existing.some((e) => e.slash === `/${slug}`)) {
    errors.push(`/${slug} already exists.`);
  }
  return errors;
}

/** PURE — canonical form of a canned draft (slug forced to /slug). */
export function cannedFromDraft(c: CannedDraft): CannedReply {
  const slug = c.slash
    .trim()
    .toLowerCase()
    .replace(/^\/+/, '')
    .replace(/[^a-z0-9_-]+/g, '');
  return {
    slash: `/${slug}`,
    text: c.text.trim(),
    numberScope: c.numberScope.trim() || null,
  };
}

/** PURE — the settings/wa_canned doc for upsert. */
export function cannedToDoc(items: CannedReply[]): { items: CannedReply[] } {
  return { items };
}
